import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminService from '../../service/AdminService';
import { Spinner, Tabs, Tab } from 'react-bootstrap';
import {
  ArrowLeft, User, Clock, CheckCircle, XCircle,
  AlertCircle, Building2, FileText, ChevronLeft, ChevronRight,
  ShieldCheck
} from 'lucide-react';
import { getMinutosFromHora, minutosATexto } from '../../utils/hoursUtils';
import '../../styles/view/UserHoursReport.css';

function getInitials(nombre = '', apellido = '') {
  return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase() || '?';
}

export default function UserHoursReport() {
  const { usuarioId } = useParams();
  const navigate = useNavigate();
  const [reporte, setReporte]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState('aprobadas');
  const [paginaAprobadas, setPaginaAprobadas]   = useState(1);
  const [paginaRechazadas, setPaginaRechazadas] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const fetchReporte = async () => {
      try {
        setError('');
        const data = await AdminService.getHorasAprobadasRechazadas(usuarioId);
        setReporte(data);
      } catch (e) {
        console.error(e);
        if (e.response?.status === 401)      setError('No autenticado. Por favor, inicia sesión nuevamente.');
        else if (e.response?.status === 403) setError('No tienes permisos de administrador para acceder a esta información.');
        else if (e.response?.status === 404) setError('Usuario no encontrado.');
        else                                  setError('Error al cargar el reporte de horas. Por favor, intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    if (usuarioId) fetchReporte();
  }, [usuarioId]);

  const fmt       = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';
  const fmtShort  = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: '2-digit', month: 'short', day: 'numeric' }) : 'N/A';
  const fmtDT     = (d) => d ? new Date(d).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const fmtDTShort = (d) => d ? new Date(d).toLocaleString('es-ES', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Spinner animation="border" style={{ color: 'var(--primary-color, #6366f1)' }} />
    </div>
  );

  if (error && !reporte) return (
    <div className="uhr-root">
      <div className="container-fluid p-3 p-md-4">
        <div className="uhr-alert mb-4">
          <AlertCircle size={16} /><span>{error}</span>
        </div>
        <button className="uhr-btn-back" onClick={() => navigate('/home-admin/sucursales-usuarios')}>
          <ArrowLeft size={15} /> Volver a Sucursales
        </button>
      </div>
    </div>
  );

  if (!reporte) return (
    <div className="uhr-root">
      <div className="container-fluid p-3 p-md-4">
        <div className="uhr-alert info mb-4">
          <AlertCircle size={16} /><span>No se encontró información del usuario.</span>
        </div>
        <button className="uhr-btn-back" onClick={() => navigate('/home-admin/sucursales-usuarios')}>
          <ArrowLeft size={15} /> Volver a Sucursales
        </button>
      </div>
    </div>
  );

  const aprobadasPaginadas  = reporte.horasAprobadas.slice((paginaAprobadas - 1) * pageSize, paginaAprobadas * pageSize);
  const rechazadasPaginadas = reporte.horasRechazadas.slice((paginaRechazadas - 1) * pageSize, paginaRechazadas * pageSize);
  const totalAprobPag  = Math.max(1, Math.ceil(reporte.horasAprobadas.length / pageSize));
  const totalRechPag   = Math.max(1, Math.ceil(reporte.horasRechazadas.length / pageSize));
  const tasaAprobacion = reporte.cantidadAprobadas + reporte.cantidadRechazadas > 0
    ? ((reporte.cantidadAprobadas / (reporte.cantidadAprobadas + reporte.cantidadRechazadas)) * 100).toFixed(0)
    : 0;

  const getRazonRechazo = (hora) => {
    if (hora.horasAprobaciones?.length > 0) {
      const razon = hora.horasAprobaciones.filter(a => a.accion === 'Rechazado').map(a => a.comentario).join(', ');
      return razon || null;
    }
    return hora.comentarios || null;
  };

  return (
    <div className="uhr-root">
      <div className="container-fluid p-3 p-md-4">

        {/* ── HEADER ── */}
        <div className="uhr-header mb-4">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <button className="uhr-btn-back-header" onClick={() => navigate('/home-admin/sucursales-usuarios')}>
                <ArrowLeft size={16} />
              </button>
              <div>
                <h3>Reporte de Horas</h3>
                <p className="mt-1">Historial detallado de horas aprobadas y rechazadas</p>
              </div>
            </div>
            <div className="uhr-header-badge">
              <FileText size={14} />
              <span>{reporte.cantidadAprobadas + reporte.cantidadRechazadas} registro{reporte.cantidadAprobadas + reporte.cantidadRechazadas !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div className="uhr-alert mb-4">
            <AlertCircle size={16} /><span>{error}</span>
            <button className="uhr-alert-close" onClick={() => setError('')}><XCircle size={14} /></button>
          </div>
        )}

        {/* ── USER INFO CARD ── */}
        <div className="uhr-card mb-4">
          <div className="uhr-card-header">
            <User size={16} />
            <span>Información del Usuario</span>
          </div>
          <div className="uhr-card-body">
            <div className="d-flex align-items-start gap-3">
              <div className="uhr-user-avatar">
                {getInitials(reporte.usuario.nombre, reporte.usuario.apellido)}
              </div>
              <div className="row g-3 flex-grow-1">
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="uhr-info-item">
                    <span className="info-label">Nombre</span>
                    <span className="info-value">{reporte.usuario.nombre} {reporte.usuario.apellido}</span>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="uhr-info-item">
                    <span className="info-label">Cédula</span>
                    <span className="info-value">{reporte.usuario.cedula}</span>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="uhr-info-item">
                    <span className="info-label">Sucursal</span>
                    <span className="info-value d-flex align-items-center gap-1">
                      <Building2 size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                      {reporte.usuario.sucursal?.nombre || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="uhr-info-item">
                    <span className="info-label">Estado</span>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {reporte.usuario.isAdmin && (
                        <span className="uhr-badge warning">
                          <ShieldCheck size={11} /> Admin
                        </span>
                      )}
                      <span className={`uhr-badge ${reporte.usuario.isActive ? 'success' : 'danger'}`}>
                        {reporte.usuario.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── STAT CARDS ── */}
        <div className="row g-3 mb-4">
          {[
            {
              label: 'Total Aprobadas', value: minutosATexto(reporte.totalHorasAprobadas ?? 0),
              sub: `${reporte.cantidadAprobadas} solicitud${reporte.cantidadAprobadas !== 1 ? 'es' : ''}`,
              icon: <CheckCircle size={20} color="var(--success,#10b981)" />, bg: 'rgba(16,185,129,.1)',
            },
            {
              label: 'Total Rechazadas', value: minutosATexto(reporte.totalHorasRechazadas ?? 0),
              sub: `${reporte.cantidadRechazadas} solicitud${reporte.cantidadRechazadas !== 1 ? 'es' : ''}`,
              icon: <XCircle size={20} color="var(--danger,#ef4444)" />, bg: 'rgba(239,68,68,.1)',
            },
            {
              label: 'Total General', value: minutosATexto((reporte.totalHorasAprobadas ?? 0) + (reporte.totalHorasRechazadas ?? 0)),
              sub: `${reporte.cantidadAprobadas + reporte.cantidadRechazadas} solicitudes`,
              icon: <Clock size={20} color="var(--primary-color,#6366f1)" />, bg: 'rgba(99,102,241,.1)',
            },
            {
              label: 'Tasa Aprobación', value: `${tasaAprobacion}%`,
              sub: 'Solicitudes aprobadas',
              icon: <FileText size={20} color="var(--info,#3b82f6)" />, bg: 'rgba(59,130,246,.1)',
            },
          ].map((s, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="uhr-stat-card">
                <div className="icon-wrap" style={{ background: s.bg }}>{s.icon}</div>
                <div>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-sub d-none d-sm-block">{s.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── TABS ── */}
        <div className="uhr-tabs-card">
          <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="uhr-tabs" fill>

            {/* TAB APROBADAS */}
            <Tab eventKey="aprobadas" title={
              <span className="uhr-tab-title">
                <CheckCircle size={14} />
                <span>Aprobadas</span>
                <span className="uhr-tab-badge success">{reporte.horasAprobadas.length}</span>
              </span>
            }>
              <div className="p-3">
                {reporte.horasAprobadas.length === 0 ? (
                  <div className="uhr-empty">
                    <CheckCircle size={40} style={{ opacity: .2, color: 'var(--success)' }} />
                    <p>No hay horas aprobadas registradas.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="d-none d-md-block">
                      <div className="table-responsive">
                        <table className="uhr-table">
                          <thead>
                            <tr>
                              <th>Fecha Ingreso</th>
                              <th>Horas</th>
                              <th>Fecha Aprobación</th>
                              <th>Aprobado Por</th>
                              <th>Comentarios</th>
                              <th>Historial</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aprobadasPaginadas.map(hora => (
                              <tr key={hora.id}>
                                <td>{fmt(hora.fechaIngreso)}</td>
                                <td><span className="uhr-hours-badge success">{minutosATexto(getMinutosFromHora(hora))}</span></td>
                                <td style={{ fontSize: '.82rem', color: 'var(--text-secondary)' }}>{fmtDT(hora.aprobadaEn)}</td>
                                <td style={{ fontSize: '.85rem' }}>
                                  {hora.aprobadaPorNavigation
                                    ? `${hora.aprobadaPorNavigation.nombre} ${hora.aprobadaPorNavigation.apellido}`
                                    : <span style={{ opacity: .4 }}>—</span>}
                                </td>
                                <td style={{ fontSize: '.82rem', color: 'var(--text-secondary)' }}>
                                  {hora.comentarios || <span style={{ opacity: .4 }}>Sin comentarios</span>}
                                </td>
                                <td>
                                  {hora.horasAprobaciones?.length > 0
                                    ? hora.horasAprobaciones.map(a => (
                                        <div key={a.id} className="uhr-history-entry">
                                          <span className={`uhr-badge ${a.accion === 'Aprobado' ? 'success' : 'danger'}`}>{a.accion}</span>
                                          <span className="uhr-history-date">{fmtDT(a.fecha)}</span>
                                          {a.comentario && <div className="uhr-history-comment">{a.comentario}</div>}
                                        </div>
                                      ))
                                    : <span style={{ opacity: .4, fontSize: '.8rem' }}>Sin historial</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="d-md-none row g-2">
                      {aprobadasPaginadas.map(hora => (
                        <div key={hora.id} className="col-12">
                          <div className="uhr-mobile-card">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="uhr-hours-badge success">{minutosATexto(getMinutosFromHora(hora))}</span>
                              <span style={{ fontSize: '.75rem', color: 'var(--text-secondary)' }}>{fmtShort(hora.fechaIngreso)}</span>
                            </div>
                            {hora.aprobadaEn && (
                              <div className="uhr-mobile-row"><span>Aprobado el</span><span>{fmtDTShort(hora.aprobadaEn)}</span></div>
                            )}
                            {hora.aprobadaPorNavigation && (
                              <div className="uhr-mobile-row"><span>Por</span><span>{hora.aprobadaPorNavigation.nombre} {hora.aprobadaPorNavigation.apellido}</span></div>
                            )}
                            {hora.comentarios && (
                              <div className="uhr-mobile-comment">{hora.comentarios}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    <PaginationBar page={paginaAprobadas} total={totalAprobPag} onPrev={() => setPaginaAprobadas(p => p - 1)} onNext={() => setPaginaAprobadas(p => p + 1)} />
                  </>
                )}
              </div>
            </Tab>

            {/* TAB RECHAZADAS */}
            <Tab eventKey="rechazadas" title={
              <span className="uhr-tab-title">
                <XCircle size={14} />
                <span>Rechazadas</span>
                <span className="uhr-tab-badge danger">{reporte.horasRechazadas.length}</span>
              </span>
            }>
              <div className="p-3">
                {reporte.horasRechazadas.length === 0 ? (
                  <div className="uhr-empty">
                    <XCircle size={40} style={{ opacity: .2, color: 'var(--danger)' }} />
                    <p>No hay horas rechazadas registradas.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="d-none d-md-block">
                      <div className="table-responsive">
                        <table className="uhr-table">
                          <thead>
                            <tr>
                              <th>Fecha Ingreso</th>
                              <th>Horas</th>
                              <th>Fecha Solicitud</th>
                              <th>Razón Rechazo</th>
                              <th>Historial</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rechazadasPaginadas.map(hora => (
                              <tr key={hora.id}>
                                <td>{fmt(hora.fechaIngreso)}</td>
                                <td><span className="uhr-hours-badge danger">{minutosATexto(getMinutosFromHora(hora))}</span></td>
                                <td style={{ fontSize: '.82rem', color: 'var(--text-secondary)' }}>{fmtDT(hora.submittedAt)}</td>
                                <td style={{ fontSize: '.82rem', color: 'var(--text-secondary)' }}>
                                  {getRazonRechazo(hora) || <span style={{ opacity: .4 }}>Sin razón</span>}
                                </td>
                                <td>
                                  {hora.horasAprobaciones?.length > 0
                                    ? hora.horasAprobaciones.map(a => (
                                        <div key={a.id} className="uhr-history-entry">
                                          <span className={`uhr-badge ${a.accion === 'Aprobado' ? 'success' : 'danger'}`}>{a.accion}</span>
                                          <span className="uhr-history-date">{fmtDT(a.fecha)}</span>
                                          {a.comentario && <div className="uhr-history-comment">{a.comentario}</div>}
                                        </div>
                                      ))
                                    : <span style={{ opacity: .4, fontSize: '.8rem' }}>Sin historial</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="d-md-none row g-2">
                      {rechazadasPaginadas.map(hora => (
                        <div key={hora.id} className="col-12">
                          <div className="uhr-mobile-card">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="uhr-hours-badge danger">{minutosATexto(getMinutosFromHora(hora))}</span>
                              <span style={{ fontSize: '.75rem', color: 'var(--text-secondary)' }}>{fmtShort(hora.fechaIngreso)}</span>
                            </div>
                            {hora.submittedAt && (
                              <div className="uhr-mobile-row"><span>Solicitado</span><span>{fmtDTShort(hora.submittedAt)}</span></div>
                            )}
                            <div className="uhr-mobile-comment">
                              {getRazonRechazo(hora) || <span style={{ opacity: .4 }}>Sin razón</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <PaginationBar page={paginaRechazadas} total={totalRechPag} onPrev={() => setPaginaRechazadas(p => p - 1)} onNext={() => setPaginaRechazadas(p => p + 1)} />
                  </>
                )}
              </div>
            </Tab>

          </Tabs>
        </div>

      </div>
    </div>
  );
}

function PaginationBar({ page, total, onPrev, onNext }) {
  if (total <= 1) return null;
  return (
    <div className="uhr-pagination d-flex justify-content-between align-items-center mt-3">
      <span>Pág. {page} / {total}</span>
      <div className="d-flex gap-2">
        <button className="btn-page" disabled={page === 1} onClick={onPrev}><ChevronLeft size={14} /></button>
        <button className="btn-page" disabled={page >= total} onClick={onNext}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}